import React, { useEffect } from 'react';
import { Form, Input, Button, message, Modal, Upload, Row, Col, Image } from 'antd';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { PlusOutlined } from '@ant-design/icons';
import { AliOssUtil } from '@/utils/AliOssUtil';
import { OssScene } from '@/global/OssConfig';
import { contexts } from '@/domains';
import '@/styles/pages/profile.less';
import './ProfileStyle.css';
import { ProfileConfig } from './Config';

const PHONE_READONLY_TIP = '手机号不可自助修改，如需修改请联系圆领平台客服';

const { GlobalContext } = contexts;

type Props = {
  open: boolean;
  onClose: () => void;
};

const ProfileModal: React.FC<Props> = ({ open, onClose }) => {
  const { userInfo, updateUserInfo } = GlobalContext.useContainer();
  const [form] = Form.useForm<{
    userAvatar?: string;
    realName?: string;
    nickname?: string;
    brief?: string;
    phone?: string;
  }>();

  const store = useLocalObservable(() => ({
    avatar: undefined as { url: string; name: string } | undefined,
  }));

  useEffect(() => {
    if (!open || !userInfo) {
      return;
    }
    store.avatar = undefined;
    form.setFieldsValue({
      userAvatar: userInfo.userAvatar,
      realName: userInfo.realName ?? '',
      nickname: userInfo.nickname ?? '',
      brief: userInfo.brief,
      phone: userInfo.phone,
    });
  }, [open, userInfo, form, store]);

  const handleUpload = async (file: File) => {
    const hideLoading = message.loading('上传中', 0);
    const handleErr = () => {
      hideLoading();
      message.error('上传失败');
    };
    try {
      const url = await AliOssUtil.uploadFile(file, OssScene.avatar);
      if (url) {
        store.avatar = { url, name: file.name };
        hideLoading();
        message.success('上传成功');
      } else {
        handleErr();
      }
    } catch (err) {
      console.error(err);
      handleErr();
    }
  };

  const onFinish = async (values: { realName?: string; nickname?: string; brief?: string }) => {
    if (!userInfo?.id) {
      return;
    }
    const realName = values.realName?.trim() ?? '';
    const nickname = values.nickname?.trim() ?? '';
    if (!realName && !nickname) {
      message.warning('请至少填写姓名或昵称');
      return;
    }
    const ok = await updateUserInfo({
      id: userInfo.id,
      userAvatar: store.avatar?.url || userInfo.userAvatar,
      realName,
      nickname,
      brief: values.brief ?? '',
    });
    if (ok) {
      message.success('保存成功');
      onClose();
    }
  };

  const showPhoneReadonlyTip = (): void => {
    message.info(PHONE_READONLY_TIP, 3);
  };

  return (
    <Modal
      title="个人资料"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnHidden
      width={ProfileConfig.modalWidthPx}
    >
      <div
        className="profile-form"
        style={
          {
            '--profile-input-radius': `${ProfileConfig.inputBorderRadiusPx}px`,
            '--profile-avatar-size': `${ProfileConfig.avatarSizePx}px`,
            '--profile-save-button-height': `${ProfileConfig.saveButtonHeightPx}px`,
            '--profile-form-row-gap': `${ProfileConfig.formRowGapPx}px`,
            '--profile-save-button-top-gap': `${ProfileConfig.saveButtonTopGapPx}px`,
          } as React.CSSProperties
        }
      >
        <Form
          layout="vertical"
          onFinish={onFinish}
          form={form}
          className="profile-form-fields"
          style={{ width: '100%', maxWidth: ProfileConfig.modalWidthPx - 48, margin: '0 auto' }}
        >
          <Form.Item
            name="userAvatar"
            className="profile-form-avatar-item"
            rules={[{ required: true, message: '请上传你的头像!' }]}
          >
            <div className="avatar-image-content">
              {!store.avatar?.url && !userInfo?.userAvatar ? (
                <Row>
                  <Col>
                    <Upload
                      customRequest={(e) => handleUpload(e.file as File)}
                      listType="picture-card"
                      showUploadList={false}
                    >
                      <div className="avatar-image-content-box">
                        <PlusOutlined />
                        <div>上传</div>
                      </div>
                    </Upload>
                  </Col>
                  <Col>
                    <span>请上传个人靓照、个性化IP等有辨识度的头像哦！</span>
                  </Col>
                </Row>
              ) : (
                <div style={{ margin: 'auto' }}>
                  <Row justify="center">
                    <Col>
                      <Image
                        className="avatar-place"
                        src={store.avatar?.url || userInfo?.userAvatar}
                        alt="Avatar"
                        width={ProfileConfig.avatarSizePx}
                        style={{ borderRadius: '50%' }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Upload
                        customRequest={(e) => handleUpload(e.file as File)}
                        listType="picture-card"
                        showUploadList={false}
                        className="small-upload-image"
                      >
                        <div className="avatar-image-content-box small-avatar-button">
                          <div>更换头像</div>
                        </div>
                      </Upload>
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          </Form.Item>
          <div className="profile-name-nickname-row">
            <Form.Item label="姓名" name="realName">
              <Input placeholder="真实姓名" maxLength={32} />
            </Form.Item>
            <Form.Item label="昵称" name="nickname">
              <Input placeholder="团队内昵称" maxLength={32} />
            </Form.Item>
          </div>
          <Form.Item label="个人简介" name="brief" rules={[{ required: false }]}>
            <Input.TextArea placeholder="说出你的宣言！" />
          </Form.Item>
          <Form.Item label="手机号" name="phone">
            <Input
              prefix="+86"
              placeholder="手机号码"
              readOnly
              onClick={showPhoneReadonlyTip}
              style={{ cursor: 'pointer' }}
            />
          </Form.Item>
          <Form.Item className="profile-form-submit">
            <Button className="login-form-button" type="primary" htmlType="submit" block>
              保存
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default observer(ProfileModal);
